import { type AnalysisSubject } from "../../../../common/models/AnalysisSubject";
import { AnalysisType } from "../../../../common/models/AnalysisType";
import { type BreakdownType } from "../../../../common/models/BreakdownType";
import { type Filter } from "../../../../common/models/Filter";
import { ButtonKind, ButtonLink } from "../../../library/Button/Button";
import { IconBack } from "../../../library/icons/IconBack";
import { trackAddAnalyticsBreakdown, trackAddAnalyticsFilter, trackChangeAnalysisSubject, trackChangeAnalysisType, trackNavigateBackToDashboard, trackRemoveAnalyticsBreakdown, trackRemoveAnalyticsFilter } from "../../../tracking";
import { AnalyzeWidget } from "../widgets/analyzeWidget/AnalyzeWidget";
import { BreakdownWidget } from "../widgets/breakdownWidget/BreakdownWidget";
import { FilterWidget } from "../widgets/filterWidget/FilterWidget";
import { UsageWidget } from "../widgets/usageWidget/UsageWidget";

import classes from "./Filters.module.css";

interface Props {
    backURL: string;
    analysisType: AnalysisType;
    analysisSubject?: AnalysisSubject;
    customProperty?: string;
    filters: Partial<Filter>[];
    breakdownType?: BreakdownType;
    customProperties?: Record<string, (string | number | boolean | Date)[]>;
    disabled?: boolean;
    onAnalysisTypeChange(analysisType: AnalysisType): void;
    onAnalysisSubjectChange(analysisSubject: AnalysisSubject, customProperty?: string): void;
    onFiltersChange(filters: Filter[]): void;
    onBreakdownTypeChange(breakdownType: BreakdownType | undefined): void;
}

export function Filters({
    backURL,
    analysisType,
    analysisSubject,
    customProperty,
    filters,
    breakdownType,
    customProperties,
    disabled = false,
    onAnalysisTypeChange,
    onAnalysisSubjectChange,
    onFiltersChange,
    onBreakdownTypeChange,
}: Props) {
    function handleBackButtonClick() {
        trackNavigateBackToDashboard();
    }

    function handleTypeChange(newType: AnalysisType) {
        trackChangeAnalysisType({ analysisType: newType });

        onAnalysisTypeChange(newType);
    }

    function handleSubjectChange(analysisSubject: AnalysisSubject, customProperty?: string) {
        trackChangeAnalysisSubject({ analysisSubject, customProperty });

        onAnalysisSubjectChange(analysisSubject, customProperty);
    }

    function handleFiltersChange(newFilters: Filter[]) {
        if (filters.length < newFilters.length) {
            trackAddAnalyticsFilter({ filterType: newFilters[newFilters.length - 1].type });
        } else if (filters.length > newFilters.length) {
            trackRemoveAnalyticsFilter();
        }

        onFiltersChange(newFilters);
    }

    function handleBreakdownChange(newBreakdownType: BreakdownType) {
        if (newBreakdownType) {
            trackAddAnalyticsBreakdown({ breakdownType: newBreakdownType });
        } else {
            trackRemoveAnalyticsBreakdown();
        }

        onBreakdownTypeChange(newBreakdownType);
    }

    return (
        <div className={classes.filters}>
            <nav>
                {!disabled && (
                    <ButtonLink kind={ButtonKind.Secondary} to={backURL} onClick={handleBackButtonClick} icon={<IconBack/>}>
                        Back to Dashboard
                    </ButtonLink>
                )}
            </nav>
            <div className={classes.widgets}>
                <AnalyzeWidget
                    analysisSubject={analysisSubject}
                    customProperty={customProperty}
                    customProperties={customProperties}
                    disabled={disabled}
                    onSubjectChange={handleSubjectChange}/>
                <UsageWidget
                    analysisType={analysisType}
                    onTypeChange={handleTypeChange}
                    disabled={disabled}/>
                <FilterWidget
                    filters={filters}
                    disabled={disabled}
                    onFiltersChange={handleFiltersChange}/>
                {analysisType === AnalysisType.LatestData && (
                    <BreakdownWidget
                        analysisSubject={analysisSubject}
                        breakdownType={breakdownType}
                        onBreakdownChange={handleBreakdownChange}
                        disabled={disabled}/>
                )}
            </div>
        </div>
    );
}
